import React, { useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import HomeNav from './HomeNav'
import Footer from './Footer'

const BRAND = '#6eb446'

// ── Section block ─────────────────────────────────────────────────────────────
interface SectionProps {
  index: number
  title: string
  children: React.ReactNode
}

const Section: React.FC<SectionProps> = ({ index, title, children }) => (
  <div style={{
    display: 'flex',
    gap: '1.25rem',
    paddingBottom: '2rem',
    borderBottom: '1px solid #f1f5f9',
  }}>
    <span style={{
      flexShrink: 0,
      width: 32,
      height: 32,
      borderRadius: '50%',
      backgroundColor: '#f4fbf0',
      border: `1.5px solid #c8edb0`,
      color: BRAND,
      fontWeight: 800,
      fontSize: '0.8rem',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      marginTop: 2,
    }}>
      {index}
    </span>
    <div style={{ flex: 1 }}>
      <h2 style={{
        margin: '0 0 0.6rem',
        fontSize: '1rem',
        fontWeight: 700,
        color: '#1e293b',
      }}>
        {title}
      </h2>
      {children}
    </div>
  </div>
)

const bodyText: React.CSSProperties = {
  margin: 0,
  fontSize: '0.9rem',
  color: '#475569',
  lineHeight: 1.8,
}

// ── PrivacyPolicy ─────────────────────────────────────────────────────────────
const PrivacyPolicy: React.FC = () => {
  const { t } = useTranslation()

  useEffect(() => {
    document.body.classList.add('page-home')
    return () => document.body.classList.remove('page-home')
  }, [])

  const subItems = [
    t('privacy_policy.text3_1'),
    t('privacy_policy.text3_2'),
    t('privacy_policy.text3_3'),
    t('privacy_policy.text3_4'),
    t('privacy_policy.text3_5'),
  ]

  return (
    <>
      <HomeNav />

      {/* Hero */}
      <section style={{ backgroundColor: '#f8faf6', borderBottom: '4px solid #000', padding: '4rem 1.5rem 3rem' }}>
        <div style={{ maxWidth: 760, margin: '0 auto', textAlign: 'center' }}>
          <span style={{
            display: 'inline-block',
            padding: '4px 16px',
            borderRadius: 9999,
            backgroundColor: '#e8f5dc',
            color: BRAND,
            fontSize: '0.78rem',
            fontWeight: 700,
            letterSpacing: '0.08em',
            textTransform: 'uppercase',
            marginBottom: '1rem',
          }}>
            •
          </span>
          <h1 style={{
            margin: 0,
            fontSize: 'clamp(1.8rem, 5vw, 2.75rem)',
            fontWeight: 900,
            color: '#1e293b',
            letterSpacing: '-0.02em',
            lineHeight: 1.15,
          }}>
            {t('privacy_policy.policy')}{' '}
            <span style={{ color: BRAND }}>{t('privacy_policy.privacy')}</span>
          </h1>
        </div>
      </section>

      {/* Content */}
      <main style={{ backgroundColor: '#fff', padding: '3rem 1.5rem 5rem' }}>
        <div style={{
          maxWidth: 760,
          margin: '0 auto',
          display: 'flex',
          flexDirection: 'column',
          gap: '2rem',
        }}>

          <Section index={1} title={t('privacy_policy.title1')}>
            <p style={bodyText}>{t('privacy_policy.text1')}</p>
          </Section>

          <Section index={2} title={t('privacy_policy.title2')}>
            <p style={bodyText}>{t('privacy_policy.text2')}</p>
          </Section>

          <Section index={3} title={t('privacy_policy.title3')}>
            <p style={{ ...bodyText, marginBottom: '0.75rem' }}>{t('privacy_policy.text3')}</p>
            <ul style={{ margin: '0 0 0.75rem', paddingLeft: '1.25rem', display: 'flex', flexDirection: 'column', gap: 4 }}>
              {subItems.map((item, i) => (
                <li key={i} style={{ fontSize: '0.9rem', color: '#475569', lineHeight: 1.7 }}>{item}</li>
              ))}
            </ul>
            <p style={bodyText}>{t('privacy_policy.text3_5_1')}</p>
          </Section>

          <Section index={4} title={t('privacy_policy.title4')}>
            <p style={{ ...bodyText, marginBottom: '0.6rem' }}>{t('privacy_policy.text4')}</p>
            <p style={{ ...bodyText, marginBottom: '0.6rem' }}>{t('privacy_policy.text4_1')}</p>
            <p style={bodyText}>{t('privacy_policy.text4_2')}</p>
          </Section>

          <Section index={5} title={t('privacy_policy.title5')}>
            <p style={bodyText}>{t('privacy_policy.text5')}</p>
          </Section>

          <Section index={6} title={t('privacy_policy.title6')}>
            <p style={{ ...bodyText, marginBottom: '0.6rem' }}>{t('privacy_policy.text6')}</p>
            <p style={bodyText}>{t('privacy_policy.text6_1')}</p>
          </Section>

          <Section index={7} title={t('privacy_policy.title7')}>
            <p style={bodyText}>{t('privacy_policy.text7')}</p>
          </Section>

          <Section index={8} title={t('privacy_policy.title8')}>
            <p style={bodyText}>{t('privacy_policy.text8')}</p>
          </Section>

          <Section index={9} title={t('privacy_policy.title9')}>
            <p style={bodyText}>{t('privacy_policy.text9')}</p>
          </Section>

        </div>
      </main>

      <Footer />
    </>
  )
}

export default PrivacyPolicy
